import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { preserveRevealedResults } from "../utils/orderOfGo";

// #644 — placings/composite for ONE class. Same visibility-aware
// fetch-then-poll shape as ../hooks/useRideStatuses.js (which wraps
// common-fe's useRideStatuses), but keyed on a single classId rather than a
// set of ride ids, and reading GET /class/:classId/results — no new route.
//
// Two modes, chosen by the caller via `intervalMs`:
//   - omitted/falsy -> fetch once (for a class that's already wrapped; its
//     results won't change again once every ride is FINAL).
//   - set -> poll at that interval, pausing while the tab is hidden and doing
//     an immediate refresh on return, exactly like the ride-status poll.
// docs/score/announcer.md's freshness table: poll ONLY the current/live class.
export const RESULTS_POLL_MS = 15000;

export function useClassResults(
  classId,
  { intervalMs, enabled = true, online = true } = {},
) {
  const scoreApi = useScoreApi();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const classIdRef = useRef(classId);
  classIdRef.current = classId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;
  // #1647: mirrors `results` synchronously (assigned every render, same
  // pattern as the refs above) so refresh() below can read the LATEST
  // committed value without waiting on a setState round-trip: the
  // preserveRevealedResults latch has to compare against what is actually on
  // screen right now, not a stale value captured in refresh's own closure.
  const resultsRef = useRef(null);
  resultsRef.current = results;

  const active = Boolean(enabled) && Boolean(classId);

  // #919 — ClassResults.jsx now flips a POLLING class into fetch-once mode
  // the moment it learns (from a poll response) that the class is final,
  // instead of only ever starting a not-yet-live class in fetch-once mode.
  // Without this guard, that mode flip (intervalMs number -> falsy) re-runs
  // the effect below and its fetch-once branch calls refresh() again — an
  // extra, redundant request the instant any class crosses into final.
  // Only skip the fetch when we already have a result for THIS classId;
  // reset the moment classId/active changes so a genuinely new fetch-once
  // class still gets its own initial fetch.
  const hasResultsForIdRef = useRef(false);
  useEffect(() => {
    hasResultsForIdRef.current = false;
  }, [classId, active]);

  // #2190 — no local cache. Always fetch; a failed fetch that already has a
  // real result on screen (resultsRef.current !== null) leaves it exactly as
  // it is — a connectivity loss only stops refresh, it never blanks or
  // errors an already-rendered class. We keep exactly what renders (see
  // #1647's latch below), so a PARTIAL row still has no number and the #697
  // reveal gate (keyed on each result's own status) holds.
  const refresh = useCallback(async () => {
    const id = classIdRef.current;
    if (!id) return;
    setLoading(true);
    try {
      const rows = unwrap(
          // #3381 -- opt in to a confirmed judge's early number on a PARTIAL ride
          // (perJudge[].partialPercentage). Staff-only: this authenticated read
          // is never the public scoreboard's, which can't pass the flag.
          await apiRef.current.results.getClassResults(id, {
            params: { includePartialJudgeScores: true },
          }),
        ) || [];
      // #1647: never let a regressed snapshot (a ride's status falling back
      // to PARTIAL, or an empty array) overwrite an already-revealed class.
      const latched = preserveRevealedResults(resultsRef.current, rows);
      // Keep the mirror in step SYNCHRONOUSLY, ahead of the setState commit:
      // two refresh() calls can overlap (the interval vs the visibilitychange
      // handler below, or the effect re-running on a reconnect while a fetch
      // is still in flight), and without this the second one reads the
      // pre-latch `prev` and lets the regressed snapshot straight through.
      resultsRef.current = latched;
      setResults(latched);
      setError(null);
    } catch (err) {
      if (resultsRef.current !== null) {
        // Already showing a real result this session — a network blip must
        // not blank it, error it, or touch it at all. Leave `results` and
        // `error` exactly as they were; the next successful poll picks up
        // normally.
        return;
      }
      // #2073 — a failed fetch with nothing loaded yet used to leave
      // `results` untouched at its initial `null`, which ClassResults.jsx
      // reads as "still loading" and renders as the #714 skeleton. With no
      // retry guaranteed (fetch-once mode never retries at all; polling mode
      // only retries every RESULTS_POLL_MS, and a persistently-failing
      // endpoint never leaves that state either), that stranded the
      // announcer on a skeleton that never resolves or errors — the other
      // half of the "one copy stuck in skeleton loaders" bug (the
      // duplicate-class-header half is buildOrderOfGoRows's fix,
      // orderOfGo.js). Settle to the same latched-empty state a genuinely
      // not-yet-scored class reaches on a SUCCESSFUL empty response: the
      // #1647 latch still refuses to blank an already-revealed class, so
      // this can only ever move a class OUT of the permanent skeleton, into
      // the existing "results not final yet" state — never fabricate a
      // reveal.
      const latched = preserveRevealedResults(resultsRef.current, []);
      resultsRef.current = latched;
      setResults(latched);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (results !== null) hasResultsForIdRef.current = true;
  }, [results]);

  useEffect(() => {
    if (!active) {
      // #1647: same latch as refresh(): going inactive (classId cleared,
      // enabled flipped off) must not blank an already-revealed class either,
      // it renders the #714 loading skeleton, which is just as much a
      // vanished-results defect from the announcer's chair.
      setResults((prev) => preserveRevealedResults(prev, null));
      return undefined;
    }

    // Fetch-once mode: no interval to arm, just get the latest and stop.
    // #919 — but not if a poll already delivered a result for this class (see
    // hasResultsForIdRef above) — that data is already fresh, so re-fetching
    // here would just be a redundant extra request the instant a live class
    // crosses into final and this effect re-runs on the intervalMs flip.
    if (!intervalMs) {
      if (!hasResultsForIdRef.current) refresh();
      return undefined;
    }

    // Polling mode: never fetch (not even the initial call) while the tab is
    // hidden. The visibilitychange handler below refreshes immediately the
    // moment it comes back, so nothing is stale on return.
    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, intervalMs);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // classId (not just active) drives re-subscription so switching the live
    // class re-fetches immediately rather than waiting out the old interval.
    // `online` is kept as a dep purely as a reconnect trigger — refresh()
    // always attempts the network and leaves whatever's already rendered
    // alone on failure (see #2190 comment above), so a connectivity flip
    // still re-fetches a fetch-once class on reconnect.
  }, [classId, active, intervalMs, refresh, online]);

  return { results, loading, error, refresh };
}

export default useClassResults;
